import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Paper, Box, IconButton, Modal } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchConversationsByFlowIdFromAPI } from '../Redux/ConversationDucks';
import { getFlowConfiguration } from '../Redux/FlowDucks';
import { GetConversationMessagesOnly } from '../Redux/MessageDucks';
import WhatsAppChat from '../components/MessagingView/ChatInterface';
import Skeleton from '../components/Skeleton';
import { downloadConversationsExcel } from '../services/conversationReportApi';

const MessagingFlowPage = () => {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [convesations, setConversations] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [sortedRows, setSortedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [downloading, setDownloading] = useState(false);

  const conversationList = useSelector(store => store.conversation.conversationList);
  const flowConfiguration = useSelector(store => store.flow.flowConfiguration);
  const dispatch = useDispatch();

  useEffect(() => {
    if (flowId) {
      setLoading(true);
      dispatch(fetchConversationsByFlowIdFromAPI(flowId));
      dispatch(getFlowConfiguration(flowId));
    }
  }, [dispatch, flowId]);

  useEffect(() => {
    setConversations(conversationList);
    setLoading(false);
  }, [conversationList]);

  useEffect(() => {
    setSortedRows(filteredItems);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convesations, filterText]);

  const handleViewDetail = (msg) => {
    setSelectedConversation(msg);
    dispatch(GetConversationMessagesOnly(msg.id));
    setIsDetailModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDetailModalOpen(false);
  };

  const getDateFormat = (date) => new Date(date).toLocaleString();

  const getTelephoneFormat = (telephone) => {
    if (!telephone) return '';
    const tel = String(telephone);
    // Mantiene solo dígitos y formatea de forma simple
    return tel.replace(/\D/g, '');
  };

  const getActiveText = (active) => (active ? 'Activa' : 'Inactiva');

  const getFlowCompletedText = (flowCompleted) => (flowCompleted ? 'Sí' : 'No');

  // No calcular etapas en frontend: solo mostrar lo que entrega el backend.
  const getStageLabel = (row) => {
    const v = row?.Stage;
    return (v === undefined || v === null || String(v).trim() === '') ? '-' : String(v);
  };

  const getStageSummaryLabel = (row) => {
    const v = row?.StageSummary;
    return (v === undefined || v === null || String(v).trim() === '') ? '-' : String(v);
  };

  const getIsSuccessfulBadge = (row) => {
    if (row?.isSuccessful === true) {
      return <span style={{ background: '#e6f4ea', color: '#2e7d32', fontWeight: 600, borderRadius: 6, padding: '2px 10px', fontSize: 12 }}>Exitoso</span>;
    }
    if (row?.isSuccessful === false) {
      return <span style={{ background: '#fdecea', color: '#c62828', fontWeight: 600, borderRadius: 6, padding: '2px 10px', fontSize: 12 }}>No exitoso</span>;
    }
    return null;
  };

  const filteredItems = convesations
    // Mostrar conversaciones de forma independiente (sin deduplicar por cliente)
    .filter(item =>
      (item.clientWSP?.whatsAppId || "")
        .toLowerCase()
        .includes(filterText.toLowerCase())
    )
    // Ordenar para que el último (mayor id) quede primero
    .sort((a, b) => Number(b?.id ?? 0) - Number(a?.id ?? 0));

  const handleSort = (column, direction, currentRows) => {
    setSortedRows(currentRows);
  };

  const currentPageItems = sortedRows.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handleDownloadExcel = async () => {
    setDownloading(true);
    const rows = currentPageItems.map(row => ({
      id: row.id,
      clientPhone: getTelephoneFormat(row.clientWSP?.whatsAppId),
      accountNumber: getTelephoneFormat(row.accountWSP?.number),
      startedAt: getDateFormat(row.startedAt),
      stage: getStageLabel(row),
      stageSummary: getStageSummaryLabel(row),
      active: getActiveText(row.active),
      flowCompleted: getFlowCompletedText(row.flowCompleted),
      isSuccessful: row.isSuccessful == null ? '-' : (row.isSuccessful ? 'Sí' : 'No'),
    }));
    try {
      await downloadConversationsExcel(rows);
    } catch (e) {
      console.error('Error descargando Excel:', e);
    } finally {
      setDownloading(false);
    }
  };

  const columns = [
    {
      name: 'ID',
      selector: row => row.id,
      sortable: true,
      maxWidth: "20px"
    },
    {
      name: 'N° Cliente',
      selector: row => getTelephoneFormat(row.clientWSP.whatsAppId),
      sortable: true
    },
    {
      name: 'N° Cuenta',
      selector: row => getTelephoneFormat(row.accountWSP.number),
      sortable: true,
    },
    {
      name: 'Hora inicio',
      selector: row => getDateFormat(row.startedAt),
      sortable: true,
    },
    {
      name: 'Etapa',
      sortable: true,
      selector: row => getStageLabel(row),
      cell: row => (
        <Box>
          <span style={{ fontWeight: 600 }}>{getStageLabel(row)}</span>
        </Box>
      ),
      maxWidth: '110px',
    },
    {
      name: 'Resumen de etapa',
      sortable: true,
      selector: row => getStageSummaryLabel(row),
      cell: row => (
        <Box>
          {getIsSuccessfulBadge(row) ?? (
            <span style={{ fontSize: '13px', color: '#1D1D1F' }}>
              {getStageSummaryLabel(row)}
            </span>
          )}
        </Box>
      ),
      grow: 2,
    },
    {
      name: 'Estado',
      selector: row => getActiveText(row.active),
      sortable: true,
      cell: row => (
        <Box>
          <span className={row.active ? 'active' : 'inactive'}>
            {getActiveText(row.active)}
          </span>
        </Box>
      ),
    },
    {
      name: 'Flujo Completado',
      selector: row => getFlowCompletedText(row.flowCompleted),
      sortable: true,
    },
    {
      name: 'Acciones',
      cell: row => (
        <Box>
          <IconButton onClick={() => handleViewDetail(row)} size="small" className="apple-button apple-tooltip">
            <VisibilityIcon fontSize="small" />
            <span className="tooltiptext">Ver Conversación</span>
          </IconButton>
        </Box>
      ),
    },
  ];

  const subHeaderComponentMemo = React.useMemo(() => (
    <Box className="apple-card" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <div className="apple-search-container">
          <SearchIcon className="apple-search-icon" />
          <input
            type="text"
            placeholder="Buscar por número de cliente"
            onChange={e => setFilterText(e.target.value)}
            className="apple-search-input"
          />
        </div>

        {(String(flowId) === '1026') ? (
          <button
            type="button"
            className="apple-button"
            onClick={() => navigate(`/messaging/flow/${flowId}/venta-directa/preleads`)}
          >
            Reporte Venta Directa (PreLeads)
          </button>
        ) : null}

        <button
          type="button"
          className="apple-button"
          onClick={handleDownloadExcel}
          disabled={downloading || currentPageItems.length === 0}
        >
          {downloading ? 'Descargando...' : 'Descargar Excel'}
        </button>
      </Box>
    </Box>
  ), [flowId, navigate, downloading, currentPageItems, handleDownloadExcel]);

  const CustomLoader = () => (
      <div style={{ padding: '24px', width: '100%' }}>
        {Array.from({ length: 10 }).map((_, idx) => (
          <React.Fragment key={idx}>
            <Skeleton width='100%' height='2rem' />
            <br />
          </React.Fragment>
        ))}
      </div>
    );
  

  return (
    <div className="apple-container">
      <Paper className="apple-card">
        <DataTable
          columns={columns}
          data={filteredItems}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 30]}
          onSort={handleSort}
          onChangePage={page => setCurrentPage(page)}
          onChangeRowsPerPage={(newRowsPerPage, page) => {
            setRowsPerPage(newRowsPerPage);
            setCurrentPage(page);
          }}
          subHeader
          subHeaderComponent={subHeaderComponentMemo}
          progressPending={loading}
          progressComponent={<CustomLoader />}
          persistTableHead
          customStyles={customStyles}
        />
      </Paper>

      <Modal
        open={isDetailModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }} //  esto elimina el fondo blanco
>
        <Box
            sx={{
            maxWidth: "800px",
            margin: "auto",
            mt: 5,
            outline: "none", //  evitar borde blanco
            background: "transparent", // evita fondo blanco inicial
            boxShadow: "none", //  sin sombra inicial
            }}
        >
            {selectedConversation && (
            <WhatsAppChat
                conversation={selectedConversation}
                onClose={handleCloseModal}
            />
            )}
        </Box>
        </Modal>
    </div>
  );
};

const customStyles = {
  table: {
    style: {
      minWidth: '100%',
      maxWidth: '100%',
    },
  },
  headRow: {
    style: {
      backgroundColor: '#F2F2F7',
      borderBottom: 'none',
    },
  },
  headCells: {
    style: {
      fontSize: '12px',
      fontWeight: '600',
      color: '#86868B',
      textTransform: 'uppercase',
    },
  },
  cells: {
    style: {
      fontSize: '14px',
      color: '#1D1D1F',
    },
  },
};

export default MessagingFlowPage;

